import { useContext, useEffect, useState } from 'react'
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked'
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container'
import { IconName } from '../../../shared/interfaces/icon-picker.model'
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading'
import { LoadingPage } from '../loading-page'
import { IReductionOffset, IReductionOffsetSubmitPayload } from '../../../shared/services/reduction-offset/reduction-offset.model'
import ReductionOffsetService from '../../../shared/services/reduction-offset/reduction-offset.service'
import gaiaTheme from '../../../root-components/theme'
import { useMediaQuery } from '@mui/material'
import { IHeadCell } from '../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { AddReductionOffsetDialog } from '../../dialogs/reduction/add-reduction-offset-dialog'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons'

export const ReductionOffsetsPage = () => {
  //Service
  const reductionOffsetService = new ReductionOffsetService()
  //Hooks
  const isReportPeriodLocked = useIsReportPeriodLocked()
  //Context
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [selectedOffset, setSelectedOffset] = useState<IReductionOffset>()
  const [offsets, setOffsets] = useState<IReductionOffset[]>()
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading 
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  useEffect(() => {
    if (reportPeriodContext.lastSelectedReportId) {
      getOffsets()
    } else {
      setIsLoading(false)
    }
  }, [inputPage, perPage, change])

  const getOffsets = () => {
    reductionOffsetService.getAllWithPagination(reportPeriodContext.lastSelectedReportId!, inputPage, perPage, orderBy, order)
      .then(response => {
        setOffsets(response.rows)
        setTotalRecords(response.count)
        setIsLoading(false)
      })
      .catch(err => {
        setIsLoading(false)
        console.log(err)
      })
  }

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy, reportPeriodContext.lastSelectedReportId])


  const editClickHandler = (id: string) => {
    setSelectedOffset(offsets?.find(offset => offset.id === id))
    setOpenDialog(true)
  }

  const deleteClickHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: 'Are you sure you would like to delete this offset? This action cannot be undone.',
      buttonCopy: 'Continue',
      cancelButtonCopy: 'Cancel',
      hideCancelButton: false
    })
      .then(response => {
        if (response) {
          reductionOffsetService.remove(id)
            .then(() => {
              getOffsets()
              userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
            })
            .catch(err => {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              console.log(err)
            })
        }
      })
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    ...!isReportPeriodLocked ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : [],
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'description', numeric: false, label: 'Description', sortable: true },
    { id: 'amount', numeric: false, label: 'Amount (tCO2e)', sortable: true },
    { id: 'offsetType', numeric: false, label: 'Type', sortable: true },
    { id: 'registry', numeric: false, label: 'Registry', sortable: true },
    { id: 'vintage', numeric: false, label: 'Vintage', sortable: true },
    { id: 'certificate', numeric: false, label: 'Certificate', sortable: false },
    // { id: 'reportPeriod', numeric: false, label: 'Report Period', sortable: true },
  ];

  const offsetTypeLabels: Record<string, string> = { removal: 'Removal', avoidance: 'Avoidance' }

  const rows = !offsets ? [] : offsets.map(offset => {
    return {
      id: offset.id,
      action: (
        <TableActionButtons
          id={offset.id}
          includeDelete={true}
          includeEdit={true}
          onEdit={editClickHandler}
          onDelete={deleteClickHandler}
        />
      ),
      name: offset.name,
      description: offset.description,
      amount: offset.amountTCE,
      offsetType: offset.offsetType ? offsetTypeLabels[offset.offsetType] ?? offset.offsetType : '—',
      registry: offset.registry ?? '—',
      vintage: offset.vintage ?? '—',
      certificate: offset.uploadedResources && offset.uploadedResources.length > 0
        ? `${offset.uploadedResources.length} attached`
        : '—',
      reportPeriod: offset.reportPeriod.friendlyName
    }
  });

  //Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const closeDialogHandler = () => {
    setOpenDialog(false)
    setSelectedOffset(undefined)
  }

  // Multipart submit (REC pattern): certificate files + the JSON payload field, plus the ids of
  // existing documents marked for removal on edit.
  const buildOffsetForm = (payload: IReductionOffsetSubmitPayload, isEdit: boolean) => {
    const form = new FormData()
    for (const file of payload.files || []) {
      form.append('files', file)
    }
    form.append('reductionOffset', JSON.stringify(payload.values))
    if (isEdit) {
      form.append('removedResourceIds', JSON.stringify(payload.removedResourceIds || []))
    }
    return form
  }

  const submitDialogHandler = (payload: IReductionOffsetSubmitPayload, resetForm: () => void, isEdit: boolean) => {
    if (!isDialogSubmitting) {
      if (!isEdit) {
        setIsDialogSubmitting(true)
        reductionOffsetService.createWithFile(buildOffsetForm(payload, false))
          .then(() => {
            closeDialogHandler()
            resetForm()
            getOffsets()
            setIsDialogSubmitting(false)
            userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
          })
          .catch(err => {
            setIsDialogSubmitting(false)
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            console.log(err)
          })
      }
      if (isEdit && selectedOffset) {
        setIsDialogSubmitting(true)
        reductionOffsetService.updateWithFile(selectedOffset.id, buildOffsetForm(payload, true))
          .then(() => {
            closeDialogHandler()
            resetForm()
            getOffsets()
            setIsDialogSubmitting(false)
            userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
          })
          .catch(err => {
            setIsDialogSubmitting(false)
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            console.log(err)
          })
      }
    }
  }

  return (
    <PageOuterContainer>
      <PageHeading
        title={'Offsets'}
        hasTabs={false}
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => { setOpenDialog(true) }}
        actionButtonText='Add Offset'
        hideShowActionButton={isReportPeriodLocked === true}
        actionButtonVariant='text'
      />
      <LoadingPage pageReady={!isLoading}>
        {offsets &&
          <TableDisplay
            onOrderChange={handleOrderChange}
            isMobileMode={isMobileMode}
            headCells={headCells}
            rows={rows}
            selected={selected}
            selectByIcon={true}
            setSelected={setSelected}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage} />}
      </LoadingPage>
      {openDialog && !selectedOffset &&
        <AddReductionOffsetDialog
          open={openDialog}
          isEdit={false}
          isSubmitting={isDialogSubmitting}
          closeHandler={closeDialogHandler}
          submitHandler={submitDialogHandler} />}
      {openDialog && selectedOffset &&
        <AddReductionOffsetDialog
          open={openDialog}
          isEdit={true}
          isSubmitting={isDialogSubmitting}
          newReductionOffset={selectedOffset}
          closeHandler={closeDialogHandler}
          submitHandler={submitDialogHandler} />}
    </PageOuterContainer>
  )
}